import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';

import { IFetchQuery } from '@services/base';

import { IGpsPenaltyImport, IPenalty } from './penalty.model';

/**
 * Whether the penalty carries a justification from the carrier: a category,
 * a non-empty free-text body, at least one attachment, or a justified_at date.
 */
export const hasJustification = (penalty: IPenalty): boolean => {
  const { justification, justified_at } = penalty;
  if (justified_at) return true;
  if (!justification) return false;
  const { category, body, attachments_url } = justification;
  if (category) return true;
  if (body && body.trim().length > 0) return true;
  return Array.isArray(attachments_url) && attachments_url.length > 0;
};

/**
 * Adds the operator's explicit choice about the justification to every
 * payload of a reset to PENDING. When `remove` is undefined the prompt was not
 * shown and the payloads are returned untouched: the request carries no choice
 * and the platform keeps the justification by default.
 */
export const withRemoveJustification = (
  payloads: IFetchQuery[],
  remove?: boolean,
): IFetchQuery[] => {
  if (remove === undefined) return payloads;
  return payloads.map(payload => ({
    ...payload,
    remove_justification: remove,
  }));
};

const CSV_DATE_FORMAT = 'MM/dd/yyyy HH:mm:ss';

/**
 * Parses a CSV date cell (`MM/dd/yyyy HH:mm:ss`) to an ISO string. Returns the
 * value untouched when it is not a valid date in that format.
 */
const csvDateToIso = (value: string): string => {
  const date = parse(String(value), CSV_DATE_FORMAT, new Date());
  return isValid(date) ? date.toISOString() : value;
};

/**
 * Maps a validated GPS import row (SEN-436) to the `importGps` request body:
 * ISO dates, numeric `week` / `penalty_amount`, `tags` split on commas, the
 * preview `errors` dropped. Every other column is passed through as received
 * (the trailer plate included). Absent optional columns are omitted.
 */
export const toGpsImportPayload = (row: IGpsPenaltyImport): IFetchQuery => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, received_on, created_at, tags, ...rest } = row;
  const payload: IFetchQuery = {
    ...rest,
    order_date: csvDateToIso(row.order_date),
    week: Number(row.week),
    penalty_amount: Number(row.penalty_amount),
    tags:
      typeof tags === 'string'
        ? tags
            .split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0)
        : [],
  };
  if (received_on !== undefined && received_on !== '') {
    payload.received_on = csvDateToIso(received_on);
  }
  if (created_at !== undefined && created_at !== '') {
    payload.created_at = csvDateToIso(created_at);
  }
  return payload;
};
